import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { userRoleService } from '@/services/api/userRoleService';
import { useUser } from '@/store/useAuthStore';
import logger from '@/lib/logger';
import type { UserRole } from '@/types/auth';

// Priority determines which roles a user can assign/remove.
// Admin can manage all roles. Non-admins can only manage roles with lower priority.
// Note: lab_manager and user_manager are at same priority (4) because they're orthogonal
// management domains (labs vs users), not a hierarchy. See tech debt for full refactor.
const ROLE_PRIORITY: Record<UserRole, number> = {
  admin: 5,
  lab_manager: 4,
  supervisor: 4,
  'user-manager': 3,
  researcher: 2,
  operator: 1,
};

const AVAILABLE_ROLES: UserRole[] = ['operator', 'supervisor', 'researcher', 'lab_manager', 'user-manager', 'admin'];

const ROLE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'info' | 'warning' | 'success'> = {
  operator: 'default',
  supervisor: 'primary',
  researcher: 'info',
  lab_manager: 'secondary',
  'user-manager': 'warning',
  admin: 'secondary',
};

interface UserRoleManagerProps {
  userId: string;
  onRolesChanged?: () => void;
}

export function UserRoleManager({ userId, onRolesChanged }: UserRoleManagerProps) {
  const [roles, setRoles] = useState<string[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentUser = useUser();
  const currentUserRoles = currentUser?.roles?.length
    ? currentUser.roles
    : currentUser?.role
    ? [currentUser.role]
    : [];
  const currentUserMaxPriority = Math.max(
    0,
    ...currentUserRoles.map((r) => ROLE_PRIORITY[r as UserRole] ?? 0)
  );
  const isAdmin = currentUserMaxPriority >= ROLE_PRIORITY['admin'];

  const fetchRoles = useCallback(async () => {
    setFetching(true);
    try {
      const data = await userRoleService.getUserRoles(userId);
      setRoles(data.map((r) => r.role));
    } catch (err) {
      logger.error('[UserRoleManager] Failed to load roles:', err);
      setError('Failed to load roles');
    } finally {
      setFetching(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const refreshRoles = async () => {
    await fetchRoles();
    onRolesChanged?.();
  };

  const handleAddRole = async () => {
    if (!selectedRole) return;
    setLoading(true);
    setError(null);
    try {
      await userRoleService.addRole(userId, selectedRole);
      setAddDialogOpen(false);
      setSelectedRole('');
      await refreshRoles();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add role';
      setError(message);
      logger.error('[UserRoleManager] Failed to add role:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRole = async (roleToRemove: string) => {
    if (roles.length <= 1) {
      setError('User must have at least one role');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await userRoleService.removeRole(userId, roleToRemove);
      await refreshRoles();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to remove role';
      setError(message);
      logger.error('[UserRoleManager] Failed to remove role:', err);
    } finally {
      setLoading(false);
    }
  };

  const availableToAdd = AVAILABLE_ROLES.filter(
    (role) =>
      !roles.includes(role) &&
      (isAdmin
        ? true
        : (ROLE_PRIORITY[role] ?? 0) < currentUserMaxPriority)
  );

  if (fetching) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {roles.map((role) => {
          const canRemove =
            roles.length > 1 &&
            (isAdmin
              ? true
              : (ROLE_PRIORITY[role as UserRole] ?? 0) < currentUserMaxPriority);
          return (
            <Chip
              key={role}
              label={role.charAt(0).toUpperCase() + role.slice(1)}
              color={ROLE_COLORS[role] || 'default'}
              size="small"
              onDelete={canRemove ? () => handleRemoveRole(role) : undefined}
              deleteIcon={<CloseIcon fontSize="small" />}
            />
          );
        })}
        {availableToAdd.length > 0 && (
          <IconButton
            size="small"
            onClick={() => setAddDialogOpen(true)}
            disabled={loading}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        )}
        {loading && <CircularProgress size={16} />}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Role</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              label="Role"
            >
              {availableToAdd.map((role) => (
                <MenuItem key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddRole}
            variant="contained"
            disabled={!selectedRole || loading}
          >
            Add Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
